/**
 * Auth Context for managing authentication state
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { AUTH_MODES } from '../types';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Modal/BottomSheet state
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState(AUTH_MODES.LOGIN);
  
  // Form state
  const [identifier, setIdentifier] = useState(''); // mobile or email
  const [identifierType, setIdentifierType] = useState(null); // 'mobile' or 'email'
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Registration form state
  const [registrationData, setRegistrationData] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    dob: null,
    salutation: '',
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Flow state
  const [otpSent, setOtpSent] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [patientCode, setPatientCode] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  
  // Open auth modal/sheet with specific mode
  const openAuth = useCallback((initialMode = AUTH_MODES.LOGIN) => {
    setMode(initialMode);
    setIsOpen(true);
    resetState();
  }, []);
  
  // Close auth modal/sheet
  const closeAuth = useCallback(() => {
    setIsOpen(false);
    resetState();
  }, []);
  
  // Reset form state
  const resetState = useCallback(() => {
    setIdentifier('');
    setIdentifierType(null);
    setOtp('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setOtpSent(false);
    setIsNewUser(false);
    setPatientCode('');
    setNeedsPassword(false);
    setRegistrationData({
      firstName: '',
      lastName: '',
      mobile: '',
      email: '',
      dob: null,
      salutation: '',
    });
  }, []);
  
  // Switch between modes
  const switchMode = useCallback((newMode) => {
    setMode(newMode);
    setError('');
    setOtp('');
    setPassword('');
  }, []);
  
  // Go back to previous step
  const goBack = useCallback(() => {
    if (mode === AUTH_MODES.OTP_VERIFY) {
      switchMode(AUTH_MODES.LOGIN);
      setOtpSent(false);
      setOtp('');
      setError('');
    } else if (mode === AUTH_MODES.PASSWORD_LOGIN) {
      switchMode(AUTH_MODES.LOGIN);
      setPassword('');
      setError('');
    } else if (mode === AUTH_MODES.SET_PASSWORD || mode === AUTH_MODES.REGISTRATION_FORM) {
      switchMode(AUTH_MODES.LOGIN);
    } else if (mode === AUTH_MODES.FORGOT_PASSWORD) {
      switchMode(AUTH_MODES.LOGIN);
    }
  }, [mode, switchMode]);
  
  const value = {
    // State
    isOpen,
    mode,
    identifier,
    identifierType,
    otp,
    password,
    confirmPassword,
    registrationData,
    loading,
    error,
    otpSent,
    isNewUser,
    patientCode,
    needsPassword,
    
    // Setters
    setIdentifier,
    setIdentifierType,
    setOtp,
    setPassword,
    setConfirmPassword,
    setRegistrationData,
    setLoading,
    setError,
    setOtpSent,
    setIsNewUser,
    setPatientCode,
    setNeedsPassword,
    setMode,
    
    // Actions
    openAuth,
    closeAuth,
    switchMode,
    goBack,
    resetState,
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
}
